import React, { useEffect, useState, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CommandLineIcon } from "@heroicons/react/24/solid";
import { IoMdArrowRoundBack } from "react-icons/io";
import { CiClock2, CiViewList } from "react-icons/ci";
import { GrScorecard } from "react-icons/gr";
import { RiQuestionAnswerFill } from "react-icons/ri";
import { GiAlarmClock } from "react-icons/gi";
import { Tabs } from "flowbite-react";

import {
  useGetAllAssignmentQuestionQuery,
  useGetAssignmentQuery,
  useGetAssignmentSubmitQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
  useStartTestMutation,
} from "../../../graphql/generated.tsx";

import StudentLayout from "../../Student/Layout/index.jsx";
import AssignmentLayout from "../Layout/index.jsx";
import formatDate from "../../Admin/Helpers/formatDate.jsx";
import QuestionCard from "../Components/QuestionCard.jsx";
import NetworkTracer from "../../Practice/Components/NetworkTracer.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import GuardedActionButton from "../../Practice/Components/GuardedActionButton.jsx";
import Loader from "../../../components/Loader.jsx";
import IconInfoRow from "../Components/IconInfoRow.jsx";

const SubmissionCodeViewer = ({ code, language }) => {
  const [languageExtension, setLanguageExtension] = useState([]);
  const sourceCode = code || "// No code submitted";

  useEffect(() => {
    let active = true;
    const loadLanguage = async () => {
      const normalizedLanguage = String(language || "").toUpperCase();
      let extension = null;

      try {
        if (["CPP", "C", "C++"].includes(normalizedLanguage)) {
          const { cpp } = await import("@codemirror/lang-cpp");
          extension = cpp();
        } else if (["PYTHON", "PY"].includes(normalizedLanguage)) {
          const { python } = await import("@codemirror/lang-python");
          extension = python();
        } else if (normalizedLanguage === "JAVA") {
          const { java } = await import("@codemirror/lang-java");
          extension = java();
        } else if (["JAVASCRIPT", "JS"].includes(normalizedLanguage)) {
          const { javascript } = await import("@codemirror/lang-javascript");
          extension = javascript();
        }
      } catch {
        extension = null;
      }

      if (active) setLanguageExtension(extension ? [extension] : []);
    };

    loadLanguage();
    return () => {
      active = false;
    };
  }, [language]);

  const editorTheme = EditorView.theme({
    "&": { fontSize: "15px" },
    ".cm-content": { padding: "14px 0" },
    ".cm-gutters": { fontSize: "13px" },
  });

  return (
    <CodeMirror
      value={sourceCode}
      extensions={[...languageExtension, editorTheme]}
      theme="light"
      editable={false}
      readOnly
      minHeight="220px"
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        highlightActiveLine: false,
      }}
      className="submission-code-editor"
    />
  );
};

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const AssignmentDetails = () => {
  const { assignmentId } = useParams();
  const [assignment, setAssignments] = useState();
  const [assignmentQuestion, setAssignmentQuestion] = useState();
  const [isBrowserSupported, setIsBrowserSupported] = useState(true);
  const [isload, setIsload] = useState(true);

  // Modal state
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [fixedTimeLeft, setFixedTimeLeft] = useState(null);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const pendingNavigate = useRef(null);

  const navigate = useNavigate();
  const [startTest] = useStartTestMutation();
  const location = useLocation();

  const isOverdueFromState = location?.state?.isOverdue;

  const { data, loading } = useGetAssignmentQuery({
    variables: { id: assignmentId },
  });

  const { data: AssignmentQuestions } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId },
  });

  const studentEmail = JSON?.parse(localStorage.getItem("user"))?.email;
  const { data: student } = useGetStudentByEmailIdQuery({
    variables: { email: studentEmail },
  });

  useEffect(() => {
    setTimeout(() => setIsload(false), 2000);
  }, []);

  const { data: campus } = useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId,
  });

  const { data: AssignmentCompleted } = useGetAssignmentSubmitQuery({
    variables: {
      assignmentId,
      studentId: student?.getStudentByEmailId.id,
      campus_name: campus?.campus?.name,
    },
    skip: !student?.getStudentByEmailId || !campus?.campus,
  });

  useEffect(() => {}, [AssignmentCompleted]);

  useEffect(() => {
    if (data?.getAssignment) setAssignments(data.getAssignment);
  }, [data]);

  useEffect(() => {
    if (AssignmentQuestions?.getAllAssignmentQuestion) {
      setAssignmentQuestion(AssignmentQuestions.getAllAssignmentQuestion);
    }
  }, [AssignmentQuestions]);

  // Browser check
  useEffect(() => {
    const ua = navigator.userAgent;
    const chromeMatch = ua.match(/Chrome\/(\d+)/);
    if (chromeMatch && !ua.includes("Edg")) {
      setIsBrowserSupported(parseInt(chromeMatch[1], 10) > 80);
      return;
    }
    const firefoxMatch = ua.match(/Firefox\/(\d+)/);
    if (firefoxMatch) {
      setIsBrowserSupported(parseInt(firefoxMatch[1], 10) > 75);
      return;
    }
    const safariMatch = ua.match(/Version\/(\d+).*Safari/);
    if (safariMatch && !ua.includes("Chrome")) {
      setIsBrowserSupported(parseInt(safariMatch[1], 10) > 13);
      return;
    }
    setIsBrowserSupported(false);
  }, []);

  const durationInSeconds = assignment?.duration
    ? assignment.duration * 60 * 60
    : 0;

  const isOverdue =
    isOverdueFromState !== undefined
      ? isOverdueFromState
      : assignment?.due_date
      ? new Date(assignment.due_date) < new Date()
      : false;

  // Background timer
  useEffect(() => {
    if (
      AssignmentCompleted?.getAssignmentSubmit &&
      !AssignmentCompleted.getAssignmentSubmit?.completed &&
      assignment?.duration
    ) {
      const startedAt =
        AssignmentCompleted.getAssignmentSubmit?.startedAt ||
        localStorage.getItem(`assignment_${assignmentId}_start`);

      if (!startedAt) return;

      const endTime = new Date(
        new Date(startedAt).getTime() + durationInSeconds * 1000
      );

      const interval = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((endTime - now) / 1000);

        if (diff <= 0) {
          clearInterval(interval);
          navigate("/assignments");
          return;
        }

      }, 1000);

      return () => clearInterval(interval);
    }
  }, [AssignmentCompleted, assignment, durationInSeconds, assignmentId, navigate]);

  // Start button handler
  const handleStartTest = async (started) => {
    if (!isBrowserSupported) return;
    if (isOverdue) return;

    const now = new Date();
    const dueDate = assignment?.due_date ? new Date(assignment.due_date) : null;

    if (!dueDate) {
      if (!started) {
        await startTest({
          variables: {
            assignment_id: assignmentId,
            student_id: student?.getStudentByEmailId.id,
            campus_name: campus?.campus?.name,
            testDurationInMinutes: durationInSeconds / 60,
          },
        });
        localStorage.setItem(
          `assignment_${assignmentId}_start`,
          new Date().toISOString()
        );
      }
      navigate("test-screen");
      return;
    }

    const remainingDueTime = Math.floor((dueDate - now) / 1000);

    if (remainingDueTime <= 0) {
      navigate("/assignments");
      return;
    }

    if (durationInSeconds > remainingDueTime) {
      setFixedTimeLeft(remainingDueTime);
      pendingNavigate.current = "test-screen";
      setShowTimeWarning(true);
      return;
    }

    if (!started) {
      await startTest({
        variables: {
          assignment_id: assignmentId,
          student_id: student?.getStudentByEmailId.id,
          campus_name: campus?.campus?.name,
          testDurationInMinutes: durationInSeconds / 60,
        },
      });
      localStorage.setItem(
        `assignment_${assignmentId}_start`,
        new Date().toISOString()
      );
    }

    navigate("test-screen");
  };

  // Modal handlers
  const handleModalContinue = async () => {
    setShowTimeWarning(false);
    const dest = pendingNavigate.current;
    pendingNavigate.current = null;

    const cappedDurationMinutes = Math.max(
      0,
      Math.floor(((fixedTimeLeft ?? durationInSeconds) / 60) * 100) / 100
    );

    // Start test on confirm if not already started
    if (!AssignmentCompleted?.getAssignmentSubmit) {
      if (cappedDurationMinutes <= 0) {
        navigate("/assignments");
        return;
      }

      await startTest({
        variables: {
          assignment_id: assignmentId,
          student_id: student?.getStudentByEmailId.id,
          campus_name: campus?.campus?.name,
          testDurationInMinutes: cappedDurationMinutes,
        },
      });
      localStorage.setItem(
        `assignment_${assignmentId}_start`,
        new Date().toISOString()
      );
    }

    if (dest) navigate(dest);
  };

  const handleModalExit = () => {
    setShowTimeWarning(false);
    pendingNavigate.current = null;
    navigate("/assignments");
  };

  const formatTime = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const formatModalTime = (sec) => {
    const totalMinutes = Math.max(0, Math.ceil(sec / 60));
    return `${totalMinutes.toString().padStart(2, "0")} min`;
  };

  const formatDuration = (durationInHours) => {
    const totalMinutes = Math.round((Number(durationInHours) || 0) * 60);
    if (totalMinutes <= 0) return "0m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  if (loading || isload) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <NetworkTracer />
      <NetworkIndicator />

      {/* ── Corner Toast Warning (Style 4) ─────────────────────────── */}
      {showTimeWarning && fixedTimeLeft !== null && (
        <IconInfoRow
          showOverlay
          sidebarMode="warning"
          stepLabel="Time Warning"
          heading={formatModalTime(fixedTimeLeft)}
          subtitle="Time remaining"
          items={[
            {
              title: "Your assignment due time is shorter than the full test duration",
              desc: "If you continue, your test will run only for the time shown above and it will auto-submit when this time ends.",
            },
          ]}
          secondaryButtonText="Exit"
          primaryButtonText="Continue ->"
          onSkip={handleModalExit}
          onEnable={handleModalContinue}
        />
      )}

      {/* Main Page */}
      <div className="w-full p-0 m-0">
        <div className="relative w-full px-4 py-6 bg-white rounded-md shadow-sm md:px-6 md:py-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate("/assignments")}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <IoMdArrowRoundBack className="w-6 h-6" />
                Go Back
              </span>
            </button>

            <GuardedActionButton
              disabled={
                AssignmentCompleted?.getAssignmentSubmit?.completed ||
                isOverdue ||
                !isBrowserSupported ||
                showTimeWarning
              }
              className={`${
                isOverdue || !isBrowserSupported || showTimeWarning
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary"
              }`}
              label={
                isOverdue
                  ? "Overdue"
                  : !isBrowserSupported
                  ? "Unsupported Browser"
                  : "Start Now"
              }
              onAction={() =>
                handleStartTest(AssignmentCompleted?.getAssignmentSubmit)
              }
            />
          </div>

          <div className="absolute font-bold text-red-500 top-20 right-4">
            {isOverdue ? "This assignment is overdue" : ""}
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="inline-block p-4 bg-orange-100 rounded-lg md:p-6 max-md:w-20">
              <CommandLineIcon className="w-8 h-8 text-orange-500 md:h-12 md:w-12" />
            </div>
            <div className="md:max-w-screen-sm">
              <div className="mb-2 text-lg font-bold md:text-xl">
                {assignment?.name}
              </div>
              <div className="text-sm text-slate-500">
                {assignment?.description}
              </div>
            </div>
          </div>

          <hr className="w-full my-4 md:my-6" />

          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="flex flex-col items-center justify-center">
              <CiClock2 className="w-5 h-5 mb-2 text-slate-500" />
              <span className="mb-1 text-sm text-slate-500">Due Date</span>
              <span className="font-bold text-red-500">
                {formatDate(assignment?.due_date) || "Not Set"}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <CiViewList className="w-5 h-5 mb-2 text-slate-500" />
              <span className="mb-1 text-sm text-center text-slate-500">
                Number of Questions
              </span>
              <span className="font-bold">
                {assignmentQuestion?.length || 0}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center">
              <GiAlarmClock className="w-5 h-5 mb-2 text-slate-500" />
              <span className="mb-1 text-sm text-slate-500">Duration</span>
              <span className="font-bold">
                {formatDuration(assignment?.duration)}
              </span>
            </div>
            {AssignmentCompleted?.getAssignmentSubmit?.completed && (
              <div className="flex flex-col items-center justify-center">
                <GrScorecard className="w-5 h-5 text-slate-500" />
                <span className="mb-1 text-sm text-slate-500">Your Score</span>
                <span className="font-bold text-green-500">
                  {AssignmentCompleted?.getAssignmentSubmit?.totalScore || 0}
                </span>
              </div>
            )}
          </div>

          {/* Submissions */}
          <div className="p-4 mt-8 bg-white rounded-md md:p-8">
            <Tabs aria-label="Tabs with underline" variant="underline">
              <Tabs.Item active title="Submissions" icon={RiQuestionAnswerFill}>
                {AssignmentCompleted?.getAssignmentSubmit?.questions?.length > 0 ? (
                  <div className="space-y-4">
                    {AssignmentCompleted?.getAssignmentSubmit?.questions.map(
                      (submission, index) => {
                        const question = assignmentQuestion?.find(
                          (q) => q.id === submission.question_id
                        );
                        return (
                          <div
                            key={index}
                            className="overflow-hidden border border-gray-200 rounded-xl bg-white cursor-pointer"
                            onClick={() =>
                              setExpandedSubmissionId((currentId) =>
                                currentId === submission.question_id
                                  ? null
                                  : submission.question_id
                              )
                            }
                          >
                            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-100 border-b border-gray-200">
                              <div className="flex items-center min-w-0 gap-2">
                                <CommandLineIcon className="w-4 h-4 flex-shrink-0 text-gray-600" />
                                <div className="text-sm font-semibold text-gray-700 truncate">
                                {question?.title || `Question ${index + 1}`}
                                </div>
                              </div>
                              <div className="flex items-center flex-shrink-0 gap-3">
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">
                                  {submission.language || "Unknown"}
                                </span>
                                <span className="text-xs font-semibold text-green-700">
                                Score: {submission.score || 0}
                                </span>
                              </div>
                            </div>
                            {expandedSubmissionId === submission.question_id && (
                              <div className="bg-white">
                                <SubmissionCodeViewer
                                  code={submission.answer?.trim()}
                                  language={submission.language}
                                />
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-xl text-gray-400 opacity-60">
                    No submissions found
                  </div>
                )}
              </Tabs.Item>
            </Tabs>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default AssignmentDetails;